import type { IOperationLogEntry } from '../models/Operation';

/**
 * The audit list's shape, shared by the SPFx web part (SharePoint REST) and the web app
 * (Microsoft Graph) so both read and write the same list and history carries over.
 */

/** List URL segment; the display title is set separately. */
export const LIST_URL_NAME: string = 'SPOrganizerAuditLog';
export const LIST_TITLE: string = 'SharePoint Organizer Audit Log';

export interface IFieldDefinition {
  name: string;
  type: 'Text' | 'Note' | 'Number' | 'Boolean';
  indexed?: boolean;
}

/**
 * Columns of the audit list. Paths use Note (multi-line) because SharePoint paths can
 * reach 400 characters, beyond a Text column's 255. Created/Author are tracked by
 * SharePoint itself and back up the User and Timestamp columns.
 */
export const FIELDS: IFieldDefinition[] = [
  { name: 'OperationId', type: 'Text', indexed: true },
  { name: 'BatchId', type: 'Text', indexed: true },
  { name: 'ChangeId', type: 'Text' },
  { name: 'Action', type: 'Text' },
  { name: 'ItemKind', type: 'Text' },
  { name: 'ItemId', type: 'Text' },
  { name: 'LibraryId', type: 'Text' },
  { name: 'LibraryName', type: 'Text' },
  { name: 'SourcePath', type: 'Note' },
  { name: 'SourceParentId', type: 'Text' },
  { name: 'OriginalName', type: 'Text' },
  { name: 'DestinationPath', type: 'Note' },
  { name: 'DestinationParentId', type: 'Text' },
  { name: 'NewName', type: 'Text' },
  { name: 'Reason', type: 'Note' },
  { name: 'Confidence', type: 'Number' },
  { name: 'OperationStatus', type: 'Text' },
  { name: 'ErrorMessage', type: 'Note' },
  { name: 'Reversible', type: 'Boolean' },
  { name: 'PerformedBy', type: 'Text' },
  { name: 'OperationTimestamp', type: 'Text' },
  { name: 'UndoOf', type: 'Text' }
];

export interface IAuditListItem {
  [field: string]: unknown;
}

function text(item: IAuditListItem, field: string): string {
  const value: unknown = item[field];
  return value === null || value === undefined ? '' : String(value);
}

/** List item fields for one entry. Text columns hold at most 255 characters. */
export function toListFields(entry: IOperationLogEntry): IAuditListItem {
  const fields: IAuditListItem = {
    Title: `${entry.action}: ${entry.originalName || entry.newName}`.substring(0, 255),
    OperationId: entry.operationId,
    BatchId: entry.batchId,
    ChangeId: entry.changeId,
    Action: entry.action,
    ItemKind: entry.itemKind,
    ItemId: entry.itemId,
    LibraryId: entry.libraryId,
    LibraryName: entry.libraryName.substring(0, 255),
    SourcePath: entry.sourcePath,
    SourceParentId: entry.sourceParentId,
    OriginalName: entry.originalName.substring(0, 255),
    DestinationPath: entry.destinationPath,
    DestinationParentId: entry.destinationParentId,
    NewName: entry.newName.substring(0, 255),
    Reason: entry.reason,
    OperationStatus: entry.status,
    ErrorMessage: entry.errorMessage ?? '',
    Reversible: entry.reversible,
    PerformedBy: entry.user.substring(0, 255),
    OperationTimestamp: entry.timestamp,
    UndoOf: entry.undoOf ?? ''
  };
  if (entry.confidence !== undefined) {
    fields.Confidence = entry.confidence;
  }
  return fields;
}

export function fromListFields(item: IAuditListItem): IOperationLogEntry {
  const confidence: string = text(item, 'Confidence');
  return {
    operationId: text(item, 'OperationId'),
    batchId: text(item, 'BatchId'),
    changeId: text(item, 'ChangeId'),
    action: text(item, 'Action') as IOperationLogEntry['action'],
    itemKind: text(item, 'ItemKind') as IOperationLogEntry['itemKind'],
    itemId: text(item, 'ItemId'),
    libraryId: text(item, 'LibraryId'),
    libraryName: text(item, 'LibraryName'),
    sourcePath: text(item, 'SourcePath'),
    sourceParentId: text(item, 'SourceParentId'),
    originalName: text(item, 'OriginalName'),
    destinationPath: text(item, 'DestinationPath'),
    destinationParentId: text(item, 'DestinationParentId'),
    newName: text(item, 'NewName'),
    reason: text(item, 'Reason'),
    confidence: confidence ? Number(confidence) : undefined,
    status: text(item, 'OperationStatus') as IOperationLogEntry['status'],
    errorMessage: text(item, 'ErrorMessage') || undefined,
    reversible: item.Reversible === true,
    user: text(item, 'PerformedBy'),
    timestamp: text(item, 'OperationTimestamp'),
    undoOf: text(item, 'UndoOf') || undefined
  };
}
